import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Stepper,
  Step,
  StepLabel,
  Grid,
  MenuItem,
  IconButton,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Alert,
  Divider
} from '@mui/material';
import {
  AddPhotoAlternate,
  Delete as DeleteIcon,
  NavigateNext,
  NavigateBefore,
  Save
} from '@mui/icons-material';
import { ProductService } from '../services/ProductService';
import * as Yup from 'yup';
import { useFormik } from 'formik';

export default function AddProduct() {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [activeStep, setActiveStep] = useState(0);
  const [images, setImages] = useState([]);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const categories = [
    'engine',
    'transmission',
    'brakes',
    'suspension',
    'electrical',
    'body',
    'interior',
    'accessories'
  ];

  const conditions = [
    'New',
    'Used',
    'Refurbished'
  ];

  const validationSchema = Yup.object({
    title: Yup.string()
      .required(t('validation.fields.title')),
    description: Yup.string()
      .required(t('validation.fields.description')),
    price: Yup.number()
      .required(t('validation.fields.price'))
      .positive(t('validation.positiveNumber')),
    brand: Yup.string()
      .required(t('validation.fields.brand')),
    model: Yup.string()
      .required(t('validation.fields.model')),
    category: Yup.string()
      .required(t('validation.fields.category')),
    condition: Yup.string()
      .required(t('validation.fields.condition')),
    location: Yup.string()
      .required(t('validation.fields.location')),
    stock: Yup.number()
      .required(t('validation.fields.stock'))
      .integer(t('validation.integer'))
      .min(0, t('validation.stockMin')),
    images: Yup.array()
      .required(t('validation.fields.images')),
    year: Yup.number()
      .required(t('product.year.errors.required'))
      .min(1900, t('product.year.errors.tooOld'))
      .max(new Date().getFullYear(), t('product.year.errors.future'))
      .typeError(t('product.year.errors.invalid'))
  });

  const steps = [
    t('product.basicInfo'),
    t('product.detailsSpecs'),
    t('product.imagesPreview')
  ];

  const isStepValid = () => {
    const fieldsInSteps = {
      0: ['title', 'description', 'price', 'category', 'condition'],
      1: ['brand', 'model', 'year', 'stock', 'location'],
      2: ['images']
    };

    const currentStepFields = fieldsInSteps[activeStep];
    
    // For the images step
    if (activeStep === 2) {
      return images.length > 0;
    }

    // For other steps, check if fields are filled and valid
    return !currentStepFields.some(field => {
      const value = formik.values[field];
      return !value || formik.errors[field];
    });
  };

  const formik = useFormik({
    initialValues: {
      title: '',
      description: '',
      price: '',
      brand: '',
      model: '',
      category: '',
      condition: '',
      location: '',
      stock: '',
      images: [],
      year: ''
    },
    validationSchema,
    onSubmit: async (values) => {
      // Validate all fields before submission
      const errors = await formik.validateForm();
      
      if (Object.keys(errors).length > 0 || images.length === 0) {
        enqueueSnackbar(t('product.form.errors.validation'), {
          variant: 'error',
          anchorOrigin: {
            vertical: 'top',
            horizontal: 'center'
          },
          autoHideDuration: 5000,
          preventDuplicate: true
        });
        return;
      }

      try {
        setIsSubmitting(true);
        const product = {
          ...values,
          sellerId: currentUser.uid,
          price: parseFloat(values.price),
          stock: parseInt(values.stock),
          year: parseInt(values.year),
        };

        await ProductService.addProduct(product, images);
        enqueueSnackbar(t('product.addedSuccessProduct'), { 
          variant: 'success',
          autoHideDuration: 6000
        });
        navigate('/profile');
      } catch (err) {
        setError(err.message);
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  const handleImageUpload = (event) => {
    const files = Array.from(event.target.files);
    const validFiles = files.filter(file => file.type.startsWith('image/'));
    setImages([...images, ...validFiles].slice(0, 6)); // Limit to 6 images
  };

  const removeImage = (index) => {
    setImages(images.filter((_, i) => i !== index));
  };

  const handleNext = async () => {
    const fieldsInSteps = {
      0: ['title', 'description', 'price', 'category', 'condition'],
      1: ['brand', 'model', 'year', 'stock', 'location'],
      2: ['images']
    };

    const currentStepFields = fieldsInSteps[activeStep];

    // Touch all fields in current step
    currentStepFields.forEach(field => {
      formik.setFieldTouched(field, true, false); // Add false to prevent validation
    });

    // Validate only current step fields
    const stepErrors = await formik.validateForm();
    const hasStepErrors = currentStepFields.some(field => stepErrors[field]);

    if (!hasStepErrors && (activeStep !== 2 || images.length > 0)) {
      setActiveStep((prev) => prev + 1);
    } else {
      enqueueSnackbar(t('product.form.errors.validation'), {
        variant: 'error',
        anchorOrigin: {
          vertical: 'top',
          horizontal: 'center'
        },
        autoHideDuration: 5000,
        preventDuplicate: true
      });
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                name="title"
                label={t('product.title')}
                value={formik.values.title}
                onChange={formik.handleChange}
                error={formik.touched.title && Boolean(formik.errors.title)}
                helperText={formik.touched.title && formik.errors.title}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                name="description"
                label={t('product.description')}
                value={formik.values.description}
                onChange={formik.handleChange}
                error={formik.touched.description && Boolean(formik.errors.description)}
                helperText={formik.touched.description && formik.errors.description}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                name="price"
                label={t('product.price')}
                type="number"
                value={formik.values.price}
                onChange={formik.handleChange}
                error={formik.touched.price && Boolean(formik.errors.price)}
                helperText={formik.touched.price && formik.errors.price}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                name="category"
                label={t('product.category')}
                value={formik.values.category}
                onChange={formik.handleChange}
                error={formik.touched.category && Boolean(formik.errors.category)}
                helperText={formik.touched.category && formik.errors.category}
              >
                {categories.map((category) => (
                  <MenuItem key={category} value={category}>
                    {t(`product.categories.${category.toLowerCase()}`)}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                name="condition"
                label={t('product.condition')}
                value={formik.values.condition}
                onChange={formik.handleChange}
                error={formik.touched.condition && Boolean(formik.errors.condition)}
                helperText={formik.touched.condition && formik.errors.condition}
              >
                {conditions.map((condition) => (
                  <MenuItem key={condition} value={condition}>
                    {t(`product.conditions.${condition.toLowerCase()}`)}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        );
      case 1:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                name="brand"
                label={t('product.brand')}
                placeholder={t('product.brandPlaceholder')}
                value={formik.values.brand}
                onChange={formik.handleChange}
                error={formik.touched.brand && Boolean(formik.errors.brand)}
                helperText={formik.touched.brand && formik.errors.brand}
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                name="model"
                label={t('product.model')}
                placeholder={t('product.modelPlaceholder')}
                value={formik.values.model}
                onChange={formik.handleChange}
                error={formik.touched.model && Boolean(formik.errors.model)}
                helperText={formik.touched.model && formik.errors.model}
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                name="year"
                label={t('product.year.label')}
                placeholder={t('product.year.placeholder')}
                type="number"
                value={formik.values.year}
                onChange={formik.handleChange}
                error={formik.touched.year && Boolean(formik.errors.year)}
                helperText={formik.touched.year && formik.errors.year}
                InputProps={{
                  inputProps: { 
                    min: 1900,
                    max: new Date().getFullYear()
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                name="stock"
                label={t('product.stock')}
                type="number"
                value={formik.values.stock}
                onChange={formik.handleChange}
                error={formik.touched.stock && Boolean(formik.errors.stock)}
                helperText={formik.touched.stock && formik.errors.stock}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                name="location"
                label={t('product.location')}
                placeholder={t('product.locationPlaceholder')}
                value={formik.values.location}
                onChange={formik.handleChange}
                error={formik.touched.location && Boolean(formik.errors.location)}
                helperText={formik.touched.location && formik.errors.location}
                margin="normal"
              />
            </Grid>
          </Grid>
        );
      case 2:
        return (
          <Box>
            <Box sx={{ mb: 3 }}>
              <input
                accept="image/*"
                style={{ display: 'none' }}
                id="image-upload"
                multiple
                type="file"
                onChange={handleImageUpload}
              />
              <label htmlFor="image-upload">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<AddPhotoAlternate />}
                  sx={{ mr: 2 }}
                >
                  {t('product.addImages')}
                </Button>
              </label>
              <Typography variant="caption" color="text.secondary">
                {t('product.uploadLimit')}
              </Typography>
            </Box>

            <ImageList cols={isMobile ? 2 : 3} gap={16}>
              {images.map((image, index) => (
                <ImageListItem 
                  key={index}
                  sx={{ 
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 1,
                    overflow: 'hidden'
                  }}
                >
                  <img
                    src={URL.createObjectURL(image)}
                    alt={`Preview ${index + 1}`}
                    loading="lazy"
                    style={{ height: 200, objectFit: 'cover' }}
                  />
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      bgcolor: 'rgba(0, 0, 0, 0.5)',
                      borderRadius: '50%'
                    }}
                  >
                    <IconButton
                      size="small"
                      onClick={() => removeImage(index)}
                      sx={{ color: 'white' }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </ImageListItem>
              ))}
            </ImageList>
          </Box>
        );
      default:
        return 'Unknown step';
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
        <Typography variant="h4" gutterBottom align="center" fontWeight="bold">
          {t('product.addNewProduct')}
        </Typography>

        <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <form onSubmit={formik.handleSubmit}>
          {getStepContent(activeStep)}

          <Divider sx={{ my: 4 }} />

          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Button
              disabled={activeStep === 0}
              onClick={handleBack}
              startIcon={<NavigateBefore />}
            >
              {t('product.back')}
            </Button>
            <Box sx={{ display: 'flex', gap: 2 }}>
              {activeStep === steps.length - 1 ? (
                <Button
                  variant="contained"
                  type="submit"
                  disabled={isSubmitting || !isStepValid()}
                  startIcon={isSubmitting ? <CircularProgress size={20} /> : <Save />}
                >
                  {isSubmitting ? t('product.saving') : t('common.save')}
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={handleNext}
                  endIcon={<NavigateNext />}
                  disabled={!isStepValid()}
                >
                  {t('product.next')}
                </Button>
              )}
            </Box>
          </Box>
        </form>
      </Paper>
    </Container>
  );
} 